import type { NavFn } from '../pages/SchedulerPage'
import { useNavigate } from 'react-router'
import { Alert, Button, TextLink } from '@indipia/ui-core'
import { useScheduleSave, type SavePayload } from '../hooks/useScheduleSave'
import { ScheduleReplaceModal } from '../components/ScheduleReplaceModal'
import { StepIndicator } from './SiteSelectView'

/**
 * AssignPreviewView — 자동 배정 저장 전 확인 화면.
 *
 * 공종 선택에서 자동 배정이 켜진 채 [스케줄 생성]을 누르면 거치는 단계. preview-assign 결과
 * (공종별 배정 시공파트너·자재파트너와 근거)를 보여 주고, [스케줄 생성하기]로 저장한 뒤 간트로 이동한다.
 * [이전]은 공종 선택으로 돌아가며 입력은 유지된다.
 */

export interface PreviewAssignment {
  workTypeCode: string
  workTypeName: string
  startDate: string
  endDate: string
  partnerId: number | null
  partnerName: string | null
  reasonType: string            // MAIN / CONTINUITY / SOLE_CANDIDATE / SCORED / UNASSIGNED
  avgRating: number | null
  collabCount: number | null
  note: string | null
  // 자재파트너 배정(발주 Y 공종만 채워짐) — null이면 직접발주 대상 아님
  vendorId: number | null
  vendorName: string | null
  vendorReasonType: string | null  // SAME_SITE / MAIN_VENDOR / RECENT_VENDOR / RECOMMENDED / UNASSIGNED
}

export interface PreviewAssignResult {
  siteId: number
  siteName: string
  customerName: string
  startDate: string
  endDate: string
  assignments: PreviewAssignment[]
}

function reasonText(a: PreviewAssignment): string {
  switch (a.reasonType) {
    case 'MAIN':       return '주시공파트너 지정'
    case 'CONTINUITY': return '같은 공종 연속 배정'
    case 'SOLE_CANDIDATE': return '유일한 후보 · 바로 배정'
    case 'SCORED': {
      const parts: string[] = []
      // 우선순위 기준(협업 횟수)을 먼저, 별점은 참고
      if (a.collabCount != null) parts.push(`협업 ${a.collabCount}회`)
      if (a.avgRating != null) parts.push(`★${a.avgRating.toFixed(1)}`)
      return parts.length ? parts.join(' · ') : '자동배정'
    }
    // 알 수 없는 유형: note 우선, 없으면 배정됨='자동배정' / 미배정='미배정'
    default:           return a.note || (a.partnerId != null ? '자동배정' : '미배정')
  }
}

// 자재파트너 배정근거
function vendorReasonText(a: PreviewAssignment): string {
  switch (a.vendorReasonType) {
    case 'SAME_SITE':    return '같은 현장 같은 공종 거래처'
    case 'MAIN_VENDOR':  return '주거래처 지정'
    case 'RECENT_VENDOR': return '최근 거래처'
    case 'RECOMMENDED':  return '추천 1순위(거래 많은 순)'
    default:            return '미매핑'
  }
}

type Props = {
  navigate: NavFn
  result: PreviewAssignResult | null
  /** 저장에 쓸 값(공종 선택 화면이 넘김) — 없으면 새로고침 등으로 입력이 사라진 상태 */
  payload: SavePayload | null
  /** 저장 성공 — 현장 목록 갱신·간트 이동은 부모가 처리 */
  onCreated?: (scheduleId: number) => void
}

export function AssignPreviewView({ navigate, result, payload, onCreated }: Props) {
  const routerNavigate = useNavigate()
  const { saving, replaceOpen, replaceScheduleId, save, confirmReplace, cancelReplace } = useScheduleSave(scheduleId => {
    if (onCreated) onCreated(scheduleId)
    else navigate('gantt', scheduleId)
  })
  // 공종 선택으로 복귀(입력은 부모에 보관). 입력이 사라졌으면 현장 선택부터 다시 시작.
  const backView = payload ? (payload.flow === 'auto' ? 'create-extract2' : 'create-extract') : 'create-site'

  if (!result || !payload) {
    return (
      <div style={{ padding: 24 }}>
        <p style={{ color: 'var(--shell-on-surface-2)' }}>배정 결과가 없습니다. 이전 단계에서 다시 시도해주세요.</p>
        <Button variant="secondary" size="sm" onClick={() => navigate(backView)} style={{ marginTop: 12 }}>
          이전
        </Button>
      </div>
    )
  }

  const assigned = result.assignments.filter(a => a.partnerId != null).length
  const total = result.assignments.length
  // 배정된 시공파트너가 한 명도 없으면(후보 0명) 시공파트너 등록 안내
  const noPartnerAtAll = total > 0 && assigned === 0

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: 24 }}>
      {/* 배정 확인은 3단계(공종·공사일)에 속함 — F1 이동 규칙 §5 */}
      <StepIndicator step={2} />

      {/* 헤더 */}
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 18, fontWeight: 800, color: 'var(--shell-on-surface)' }}>
          자동배정 확인 <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--shell-on-surface-2)' }}>(시공파트너 · 자재파트너 · 저장 전 확인)</span>
        </h1>
        <p style={{ marginTop: 4, fontSize: 13, color: 'var(--shell-on-surface-2)' }}>
          {result.siteName}{result.customerName ? ` · ${result.customerName}` : ''} · {result.startDate} ~ {result.endDate}
          {'  '}· 배정 {assigned}/{total}
        </p>
        {assigned < total && (
          <p style={{ marginTop: 4, fontSize: 12, color: 'var(--shell-on-surface-2)' }}>
            배정되지 않은 공종은 저장한 뒤 간트에서 직접 지정할 수 있어요
          </p>
        )}
      </div>

      {noPartnerAtAll && (
        <Alert
          kind="warning"
          title="빨리 사람 섭외 & 시공파트너 등록"
          action={<TextLink onClick={() => routerNavigate('/partners/new')}>시공파트너 등록 →</TextLink>}
          style={{ marginBottom: 'var(--gap-md)' }}
        >
          배정할 수 있는 시공파트너가 없어요. 시공파트너를 등록하면 다음 생성부터 자동으로 배정돼요.
        </Alert>
      )}

      {/* 리스트 — 공종 · 시공파트너(근거) · 자재파트너(근거) */}
      <div style={{ border: '1px solid var(--shell-border)', borderRadius: 8, overflow: 'hidden' }}>
        {/* 헤더행 */}
        <div style={{ display: 'flex', gap: 12, padding: '8px 14px', background: 'var(--shell-bg)', borderBottom: '1px solid var(--shell-border)', fontSize: 11, fontWeight: 700, color: 'var(--shell-on-surface-2)' }}>
          <span style={{ width: 120, flexShrink: 0 }}>공종</span>
          <span style={{ flex: 1, minWidth: 0 }}>시공파트너</span>
          <span style={{ flex: 1, minWidth: 0 }}>자재파트너</span>
        </div>
        {result.assignments.map((a, i) => {
          const noForeman = a.partnerId == null
          const isOrder = a.vendorReasonType != null       // 발주 Y 공종(자재 대상)
          const noVendor = a.vendorId == null
          return (
            <div
              key={`${a.workTypeCode}-${i}`}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px',
                borderTop: i === 0 ? 'none' : '1px solid var(--shell-border)',
                background: 'var(--shell-surface)',
              }}
            >
              {/* 공종명 */}
              <span style={{ width: 120, flexShrink: 0, fontWeight: 700, fontSize: 13, color: 'var(--shell-on-surface)' }}>
                {a.workTypeName}
              </span>
              {/* 시공파트너: 이름 + 근거 */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: noForeman ? 'var(--shell-hint)' : 'var(--shell-on-surface)' }}>
                  {noForeman ? '— 미배정' : a.partnerName}
                </div>
                {!noForeman && <div style={{ fontSize: 11, color: 'var(--shell-on-surface-2)' }}>{reasonText(a)}</div>}
                {noForeman && a.note && <div style={{ fontSize: 11, color: 'var(--shell-on-surface-2)' }}>{a.note}</div>}
              </div>
              {/* 자재파트너: 거래처 + 근거 (발주 대상 아니면 '—') */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {!isOrder ? (
                  <div style={{ fontSize: 13, color: 'var(--shell-hint)' }}>— 발주 없음</div>
                ) : (
                  <>
                    <div style={{ fontSize: 13, color: noVendor ? 'var(--shell-hint)' : 'var(--shell-on-surface)' }}>
                      {noVendor ? '— 미매핑' : a.vendorName}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--shell-on-surface-2)' }}>{vendorReasonText(a)}</div>
                  </>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <Button variant="secondary" size="lg" className="flex-1" onClick={() => navigate(backView)} disabled={saving}>
          이전
        </Button>
        <Button variant="primary" size="lg" className="flex-[2]" onClick={() => void save(payload)} loading={saving} disabled={saving}>
          {saving ? '생성 중...' : '스케줄 생성하기'}
        </Button>
      </div>

      <ScheduleReplaceModal open={replaceOpen} onCancel={cancelReplace} onConfirm={confirmReplace} existingScheduleId={replaceScheduleId} />
    </div>
  )
}
